import { Box, Button, Collapse, Divider, Grid, Stack, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback } from 'react'
import { useField, useFormikContext } from 'formik'
import { SwitchField, TextField } from 'src/Libs/inputs/formik'
import Select from 'src/Libs/inputs/formik/Select'
import WeekDaySelector from 'src/Elements/Selectors/WeekDaySelector'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { PeriodChargeType } from 'src/Libs/smart-charge/api/types'
import { LabeledOption } from 'src/Types/globalTypes'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY } from 'src/Libs/farm/constants'
import { Day, Days } from '@e3dc-react/shell/Libs/date/types'
import { makeStyles } from 'tss-react/mui'

// this ensures the hh:mm time format without seconds
const STEP_SIZE_IN_SECONDS = 60

const useStyles = makeStyles()(theme => ({
  weekDaySelector: {
    flexFlow: 'wrap',
  },
}))

interface Props {
  name: string
  onRemove: () => void
  hasWritingRights?: boolean
}

const EmsIdlePeriodListItem: React.FC<Props> = ({ name, onRemove, hasWritingRights = true }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const theme = useTheme()
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))

  const { setFieldValue } = useFormikContext()
  const startFieldName = `${name}.startTimeOfDay`
  const endFieldName = `${name}.endTimeOfDay`
  const dayOfWeekFieldName = `${name}.daysOfWeek`
  const [isActiveField] = useField<boolean>(`${name}.isActive`)
  const [daysOfWeekField] = useField<Days>(dayOfWeekFieldName)

  const selectableTypeOptions: LabeledOption<PeriodChargeType>[] = Object.values(PeriodChargeType)
    .filter(value => typeof value !== 'string')
    .map(value => ({ value, label: t(`smartCharge.periodType.${value}`) } as LabeledOption<PeriodChargeType>))

  const handleSelectDay = useCallback(
    (dayName: Day, selected: boolean): void => {
      setFieldValue(dayOfWeekFieldName, { ...daysOfWeekField.value, [dayName]: selected })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [daysOfWeekField]
  )

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={'column'} sx={{ flex: 1 }}>
        <Stack direction={'row'} justifyContent={'flex-end'} alignItems={'center'} sx={{ mb: 2, mr: 0 }}>
          <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights}>
            <Button color={'error'} variant={'text'} onClick={onRemove} disabled={!hasWritingRights}>
              {t('common.remove')}
            </Button>
          </OptionalTooltipWrapper>
          <Divider orientation={'vertical'} sx={theme => ({ mr: 2, borderColor: theme.palette.grey.grey06 })} flexItem />
          <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights}>
            <SwitchField name={`${name}.isActive`} disabled={!hasWritingRights} sx={{ mr: 0 }} />
          </OptionalTooltipWrapper>
        </Stack>
        <Collapse in={isActiveField.value}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Stack spacing={2}>
                <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights}>
                  <TextField
                    label={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.form.description')}
                    name={`${name}.description`}
                    disabled={!isActiveField.value || !hasWritingRights}
                  />
                </OptionalTooltipWrapper>

                <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights}>
                  <Select
                    label={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.form.type')}
                    options={selectableTypeOptions}
                    name={`${name}.type`}
                    disabled={!isActiveField.value || !hasWritingRights}
                  />
                </OptionalTooltipWrapper>
              </Stack>
            </Grid>
            <Grid item md={6} xs={12}>
              <Stack spacing={2}>
                <Stack direction={upSm ? 'row' : 'column'} spacing={2}>
                  <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights} fullWidth>
                    <TextField
                      label={t('common.from')}
                      type="time"
                      step={STEP_SIZE_IN_SECONDS}
                      name={startFieldName}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isActiveField.value || !hasWritingRights}
                    />
                  </OptionalTooltipWrapper>

                  <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights} fullWidth>
                    <TextField
                      label={t('common.until')}
                      type="time"
                      name={endFieldName}
                      step={STEP_SIZE_IN_SECONDS}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isActiveField.value || !hasWritingRights}
                    />
                  </OptionalTooltipWrapper>
                </Stack>
                <OptionalTooltipWrapper titleKey={SETTING_RESTRICTED_TO_SERVER_TRANSLATION_KEY} hasTooltip={!hasWritingRights}>
                  <WeekDaySelector
                    days={daysOfWeekField.value}
                    disabledDays={[]}
                    onSelectDay={handleSelectDay}
                    disabled={!isActiveField.value || !hasWritingRights}
                    className={classes.weekDaySelector}
                  />
                </OptionalTooltipWrapper>
              </Stack>
            </Grid>
          </Grid>
        </Collapse>
      </Stack>
    </Box>
  )
}

export default EmsIdlePeriodListItem
